"use client";

import { c as _c } from "react/compiler-runtime";
import { useAuth } from "@/components/auth/Auth";
import { ApiWavesV2ListType } from "@/generated/models/ApiWavesV2ListType";
import { fetchWavesV2Page } from "@/services/api/waves-v2-api";
import { ArrowRightIcon } from "@heroicons/react/24/outline";
import { useQuery } from "@tanstack/react-query";
import Link from "next/link";
import { ExploreWaveCard } from "./ExploreWaveCard";
import { ExploreWaveCardSkeleton } from "./ExploreWaveCardSkeleton";
const DEFAULT_WAVES_LIMIT = 6;
interface ExploreWavesSectionProps {
  readonly title?: string | undefined;
  readonly subtitle?: string | null | undefined;
  readonly limit?: number | undefined;
  readonly viewAllHref?: string | null | undefined;
  readonly excludeFollowed?: boolean | undefined;
}
export function ExploreWavesSection(t0) {
  const $ = _c(33);
  const {
    title: t1,
    subtitle: t2,
    limit: t3,
    viewAllHref: t4,
    excludeFollowed: t5
  } = t0;
  const title = t1 === undefined ? "Tired of bot replies? Join the most interesting chats in crypto" : t1;
  const subtitle = t2 === undefined ? "Most active waves" : t2;
  const limit = t3 === undefined ? DEFAULT_WAVES_LIMIT : t3;
  const viewAllHref = t4 === undefined ? "/waves" : t4;
  const excludeFollowed = t5 === undefined ? false : t5;
  const {
    connectedProfile
  } = useAuth();
  const userScope = connectedProfile?.id ?? connectedProfile?.normalised_handle ?? connectedProfile?.handle ?? null;
  let t6;
  if ($[0] !== excludeFollowed || $[1] !== limit || $[2] !== userScope) {
    t6 = ["explore-waves", ApiWavesV2ListType.Hot, limit, excludeFollowed, userScope];
    $[0] = excludeFollowed;
    $[1] = limit;
    $[2] = userScope;
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  let t7;
  if ($[4] !== excludeFollowed || $[5] !== limit) {
    t7 = async () => {
      const page = await fetchWavesV2Page({
        view: ApiWavesV2ListType.Hot,
        page: 1,
        pageSize: limit,
        excludeFollowed
      });
      return page.waves;
    };
    $[4] = excludeFollowed;
    $[5] = limit;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  const t8 = excludeFollowed ? 0 : 300000;
  let t9;
  if ($[7] !== excludeFollowed) {
    t9 = excludeFollowed ? {
      gcTime: 0
    } : {};
    $[7] = excludeFollowed;
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  let t10;
  if ($[9] !== t6 || $[10] !== t7 || $[11] !== t8 || $[12] !== t9) {
    t10 = {
      queryKey: t6,
      queryFn: t7,
      staleTime: t8,
      ...t9
    };
    $[9] = t6;
    $[10] = t7;
    $[11] = t8;
    $[12] = t9;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  const {
    data: waves,
    isLoading,
    isError
  } = useQuery(t10);
  if (isError) {
    return null;
  }
  if (!isLoading && (!waves || waves.length === 0)) {
    return null;
  }
  let t11;
  if ($[14] !== title) {
    t11 = <span className="tw-m-0 tw-text-xl tw-font-semibold tw-tracking-tight tw-text-iron-200 md:tw-text-2xl">{title}</span>;
    $[14] = title;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] !== subtitle) {
    t12 = subtitle && <p className="tw-mb-0 tw-mt-2 tw-text-base tw-text-iron-500">{subtitle}</p>;
    $[16] = subtitle;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  let t13;
  if ($[18] !== t11 || $[19] !== t12) {
    t13 = <div className="tw-mb-8 tw-flex tw-flex-col tw-items-start tw-gap-4 md:tw-items-end"><div className="tw-max-w-sm md:tw-mx-auto md:tw-max-w-xl md:tw-text-center lg:tw-max-w-full">{t11}{t12}</div></div>;
    $[18] = t11;
    $[19] = t12;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  let t14;
  if ($[21] !== isLoading || $[22] !== limit || $[23] !== waves) {
    t14 = isLoading ? Array.from({
      length: limit
    }).map(_temp) : waves?.map(_temp2);
    $[21] = isLoading;
    $[22] = limit;
    $[23] = waves;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] !== t14) {
    t15 = <div className="tw-grid tw-grid-cols-1 tw-gap-x-3 tw-gap-y-4 sm:tw-grid-cols-2 sm:tw-gap-6 lg:tw-grid-cols-3">{t14}</div>;
    $[25] = t14;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[27] !== viewAllHref) {
    t16 = viewAllHref && <div className="tw-mt-8 tw-flex tw-justify-center md:tw-mt-10"><Link href={viewAllHref} className="tw-inline-flex tw-items-center tw-gap-1.5 tw-text-sm tw-font-medium tw-text-iron-400 tw-no-underline tw-transition-colors hover:tw-text-white"><span>View all</span><ArrowRightIcon className="tw-size-4 tw-flex-shrink-0" aria-hidden={true} /></Link></div>;
    $[27] = viewAllHref;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  let t17;
  if ($[29] !== t13 || $[30] !== t15 || $[31] !== t16) {
    t17 = <section className="tw-px-4 tw-py-10 md:tw-px-6 md:tw-py-16 lg:tw-px-8"><div>{t13}{t15}{t16}</div></section>;
    $[29] = t13;
    $[30] = t15;
    $[31] = t16;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  return t17;
}
function _temp2(wave) {
  return <div key={wave.id} className="tw-w-full"><ExploreWaveCard wave={wave} /></div>;
}
function _temp(_, index) {
  return <div key={`skeleton-${index}`} className="tw-w-full"><ExploreWaveCardSkeleton /></div>;
}